import React from "react";

export default function ApiIntroduction() {
  return (
    <div className="text-gray-800 text-md leading-7 px-4 min-w-0 max-w-[40rem] lg:mt-0 lg:mr-0 lg:max-w-[50rem]">
      {/* Hero / Title */}
      <header className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-transparent" />
        <div className="relative p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            LiveTakeoff API — Introduction
          </h1>
          <p className="mt-3 max-w-3xl text-base sm:text-lg leading-7 text-slate-600">
            The LiveTakeoff REST API powers a workflow platform for scheduling,
            coordinating, and tracking aircraft cleaning and detailing
            operations at scale. It enables customers, internal coordinators,
            and vendor project managers to create and manage jobs, attach files
            and comments, and synchronize operational data with external
            systems.
          </p>

          {/* Quick facts */}
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Style
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                REST • JSON
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Auth
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">JWT</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-slate-500">
                Versioning
              </div>
              <div className="mt-1 font-mono text-sm text-slate-900">
                /api/ (e.g., /api/jobs/)
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mt-10 rounded-3xl border border-slate-200 bg-white p-6 sm:p-10 shadow-sm">
        {/* What this API enables */}
        <section>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            What this API enables
          </h2>
          <p className="mt-3 leading-7 text-slate-700">
            Integrators and internal tools can leverage the API to orchestrate
            end-to-end job workflows: submit requests, assign vendors, track
            work-in-progress, attach artifacts, finalize completions, and
            reconcile invoices. The API favors predictability and clear resource
            modeling so you can build reliable operational automations.
          </p>
          <ul className="mt-4 grid list-disc gap-3 pl-5 text-slate-700 marker:text-slate-400">
            <li>Create and manage jobs, services, and retainers.</li>
            <li>Attach files and comments for operational visibility.</li>
            <li>Coordinate vendors, airports, and FBO data.</li>
            <li>Access pricing and invoicing details post-completion.</li>
          </ul>
        </section>

        {/* Design principles */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Design principles
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Predictable
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Resource-oriented endpoints, standard HTTP verbs, consistent
                response envelopes, and sensible pagination.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">Secure</div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                JWT-based authentication, role/permission checks, and
                audit-friendly activity logs across critical operations.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Practical
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                Filtering, ordering, and pagination designed for high-volume
                operational datasets and real-world latency constraints.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-sm font-semibold text-slate-900">
                Extensible
              </div>
              <p className="mt-1 text-sm leading-6 text-slate-600">
                First-class relationships (jobs, services, tails, vendors) for
                evolving workflows and integration touchpoints.
              </p>
            </div>
          </div>
        </section>

        {/* Who it's for */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Who it’s for
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[
              [
                "Customers",
                "Submit/manage requests, review progress, and share artifacts.",
              ],
              [
                "Internal Teams",
                "Coordinate vendors, manage SLAs, and reconcile invoices.",
              ],
              [
                "Vendors / PMs",
                "Accept work, perform services, upload files, complete jobs.",
              ],
              [
                "Systems Integrators",
                "Connect LiveTakeoff with CRMs, accounting, and data tools.",
              ],
            ].map(([title, desc]) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-5"
              >
                <div className="text-sm font-semibold text-slate-900">
                  {title}
                </div>
                <p className="mt-1 text-sm leading-6 text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Conventions (short + linkless) */}
        <section className="mt-10">
          <h2 className="text-xl font-bold tracking-tight text-slate-900">
            Conventions at a glance
          </h2>
          <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-700">
            <li>
              <span className="font-semibold">Content-Type:</span>{" "}
              <code className="font-mono">application/json</code> (uploads:
              multipart form).
            </li>
            <li>
              <span className="font-semibold">Timestamps:</span> ISO-8601 UTC.
            </li>
            <li>
              <span className="font-semibold">Pagination:</span>{" "}
              <code className="font-mono">count</code>,{" "}
              <code className="font-mono">next</code>,{" "}
              <code className="font-mono">previous</code>.
            </li>
          </ul>
        </section>
        <section className="mt-10">
          <h3
            id="authentication"
            className="text-xl font-bold tracking-tight text-slate-900"
          >
            Authentication
          </h3>

          <p className="mt-4">
            You'll need to authenticate your requests to access most of the
            endpoints in the LiveTakeofff API. We are currently using JWT
            authentication. Use the access token obtained from the token
            endpoint to authenticate your requests by including it in the
            Authorization header as JWT your_access_token. Notice the space
            between JWT and your token.
          </p>

          <div className="not-prose my-6 overflow-hidden rounded-2xl bg-zinc-900 shadow-md dark:ring-1 dark:ring-white/10">
            <div
              className="flex min-h-[calc(theme(spacing.12)+1px)]
                                 flex-wrap items-start gap-x-4 border-b border-zinc-700
                                bg-zinc-800 px-4 dark:border-zinc-800 dark:bg-transparent"
            >
              <h3 className="mr-auto pt-3 text-sm font-semibold text-white relative top-1">
                Request
              </h3>
            </div>

            <div className="flex h-9 items-center gap-2 px-4 border-b border-zinc-700">
              <div className="dark flex">
                <span className="font-mono text-[0.625rem] font-semibold leading-6 text-emerald-500 dark:text-emerald-400">
                  POST
                </span>
              </div>

              <span className="h-0.5 w-0.5 rounded-full bg-zinc-500"></span>

              <span className="font-mono text-xs text-zinc-400">
                /api/token/
              </span>
            </div>

            <div className="group dark:bg-white/2.5 pb-3">
              <div className="relative">
                <pre className="overflow-x-auto p-4 text-xs text-white mt-2">
                  <code className="language-bash">
                    <div>
                      curl -P https://api-livetakeoff.herokuapp.com/api/token/ \
                    </div>

                    <div className="mt-2">
                      <span> -H </span>

                      <span style={{ color: "#6ee7b7" }}>
                        "Content-Type: application/json"
                      </span>
                    </div>

                    <div className="mt-2">
                      <span> -d </span>

                      <span style={{ color: "#6ee7b7" }}>
                        '{"{"}"
                        <span style={{ color: "#7dd3fc" }}>username</span>
                        ":"your_username","
                        <span style={{ color: "#7dd3fc" }}>password</span>
                        ":"your_password"{"}"}'
                      </span>
                    </div>
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
